import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { IDiagnosticNotification } from 'utils/interfaces'

interface Props {
  notification: IDiagnosticNotification
  onAction: (buttonId: string) => void
  /** Id нажатой кнопки, пока идёт обработка. */
  loadingButtonId?: string
}

const getSeverityColor = (code: number) => {
  if (code >= 1000 && code < 2000) return '#f44336' // Error
  if (code >= 2000 && code < 3000) return '#ff9800' // Warning
  return '#2196f3' // Info
}

/**
 * Карточка диагностического уведомления для страницы диагностики робота.
 * Отличается от общей DiagnosticNotification:
 * - растянута на всю ширину;
 * - без тени;
 * - без подписи «Robot: ...» (и так на странице конкретного робота).
 */
const RobotDiagnosticNotification = ({ notification, onAction, loadingButtonId }: Props) => {
  const [timeLeft, setTimeLeft] = useState(notification.timeout)
  const initialTimeout = notification.timeout

  useEffect(() => {
    if (timeLeft <= 0) return

    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 100))
    }, 100)

    return () => clearInterval(timer)
  }, [timeLeft])

  const color = getSeverityColor(notification.code)
  const isLoading = loadingButtonId !== undefined

  return (
    <Card
      sx={{
        width: '100%',
        borderLeft: `6px solid ${color}`,
        boxShadow: 0,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ py: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>
          {notification.message}
        </Typography>
        {initialTimeout > 0 && (
          <LinearProgress
            variant="determinate"
            value={(timeLeft / initialTimeout) * 100}
            sx={{
              mt: 0.5,
              height: 3,
              borderRadius: 2,
              backgroundColor: '#eee',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
              },
            }}
          />
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
        <Stack direction="row" spacing={1}>
          {notification.buttons.map(btn => (
            <Button
              key={btn.id}
              size="small"
              variant="outlined"
              disabled={isLoading}
              startIcon={loadingButtonId === btn.id ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={() => onAction(btn.id)}
              sx={{
                color,
                borderColor: color,
                '&:hover': {
                  borderColor: color,
                  backgroundColor: `${color}11`,
                },
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Stack>
      </CardActions>
    </Card>
  )
}

export default RobotDiagnosticNotification
